import type {
  CameraControlCaps,
  CameraControlsReported,
  CameraControlsSetting,
  CameraControlsUpdate,
} from '@/types';
import { sameMeteringBox } from '@/lib/meteringBox';

// What POST /fleet/devices/{id}/camera-controls accepts; it answers 400 outside these.
const ZOOM_LIMITS = { min: 0.5, max: 10 };
const EV_LIMITS = { min: -4, max: 4 };

export const ZOOM_SLIDER_STEP = 0.1;

export interface ControlRanges {
  zoomMin: number;
  zoomMax: number;
  evMin: number;
  evMax: number;
  evStep: number;
}

const DEFAULT_RANGES: ControlRanges = { zoomMin: 1, zoomMax: 4, evMin: -2, evMax: 2, evStep: 0.5 };

const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// Half away from zero, like the phone's EV-to-steps conversion.
const roundHalfAway = (v: number): number => Math.sign(v) * Math.round(Math.abs(v));

/**
 * Slider and stepper ranges from the caps the phone's last recording reported, kept inside what
 * the server accepts. A missing or unusable range falls back to the default.
 */
export const resolveControlRanges = (caps: CameraControlCaps | null | undefined): ControlRanges => {
  const ranges = { ...DEFAULT_RANGES };
  if (caps && finite(caps.zoom_min) && finite(caps.zoom_max)) {
    // Inward onto the slider's grid, so both ends are zooms the camera can do.
    const lo = Math.ceil(Math.max(caps.zoom_min, ZOOM_LIMITS.min) * 10 - 1e-9) / 10;
    const hi = Math.floor(Math.min(caps.zoom_max, ZOOM_LIMITS.max) * 10 + 1e-9) / 10;
    if (hi > lo) {
      ranges.zoomMin = lo;
      ranges.zoomMax = hi;
    }
  }
  if (caps && finite(caps.ev_min) && finite(caps.ev_max) && finite(caps.ev_step) && caps.ev_step > 0) {
    const lo = Math.max(caps.ev_min, EV_LIMITS.min);
    const hi = Math.min(caps.ev_max, EV_LIMITS.max);
    if (hi > lo) {
      ranges.evMin = lo;
      ranges.evMax = hi;
      ranges.evStep = caps.ev_step;
    }
  }
  return ranges;
};

export const snapZoom = (zoom: number, { zoomMin, zoomMax }: ControlRanges): number =>
  clamp(Math.round(zoom * 10) / 10, zoomMin, zoomMax);

const evIndexBounds = ({ evMin, evMax, evStep }: ControlRanges): [number, number] => [
  Math.ceil(evMin / evStep - 1e-9),
  Math.floor(evMax / evStep + 1e-9),
];

// Three decimals hide float noise (3 x 0.1) and still round back to the same step on the phone for
// any step above 0.001 EV.
const evAtIndex = (index: number, { evStep }: ControlRanges): number =>
  Math.round(index * evStep * 1000) / 1000;

/** The nearest EV on the camera's step grid, inside its range. */
export const snapEv = (ev: number, ranges: ControlRanges): number => {
  const [lo, hi] = evIndexBounds(ranges);
  return evAtIndex(clamp(roundHalfAway(ev / ranges.evStep), lo, hi), ranges);
};

/** One step down (-1) or up (+1) on the camera's step grid, inside its range. */
export const stepEv = (ev: number, direction: -1 | 1, ranges: ControlRanges): number => {
  const [lo, hi] = evIndexBounds(ranges);
  return evAtIndex(clamp(roundHalfAway(ev / ranges.evStep) + direction, lo, hi), ranges);
};

/** "1.5×", "1.0×", or "2.37×" for a value the phone clamped off the slider's grid. */
export const formatZoom = (zoom: number): string => {
  const fixed = zoom.toFixed(2);
  return `${fixed.endsWith('0') ? fixed.slice(0, -1) : fixed}×`;
};

/** "−1.5 EV" (true minus sign), "+0.3 EV", "0.0 EV". */
export const formatEv = (ev: number): string => {
  const tenths = roundHalfAway(ev * 10) / 10;
  if (tenths === 0) return '0.0 EV';
  return `${tenths < 0 ? '−' : '+'}${Math.abs(tenths).toFixed(1)} EV`;
};

/** The three controls of a stored setting; all null when there is none. */
export const controlsOf = (
  setting: CameraControlsUpdate | null | undefined,
): CameraControlsUpdate => ({
  zoom_ratio: setting?.zoom_ratio ?? null,
  ev: setting?.ev ?? null,
  metering_box: setting?.metering_box ?? null,
});

export const sameCameraControls = (a: CameraControlsUpdate, b: CameraControlsUpdate): boolean =>
  a.zoom_ratio === b.zoom_ratio && a.ev === b.ev && sameMeteringBox(a.metering_box, b.metering_box);

const hasAnyControl = (c: CameraControlsUpdate): boolean =>
  c.zoom_ratio != null || c.ev != null || c.metering_box != null;

/**
 * Whether the stored setting has yet to reach a recording. A recording reports the revision in
 * force at its start, or null when none was; a cleared setting leaves the phone's bundle without
 * camera controls, so its recordings report null rather than the clear's revision.
 */
export const settingAwaitsRecording = (
  setting: CameraControlsSetting | null | undefined,
  reported: CameraControlsReported | null | undefined,
): boolean => {
  if (!setting) return false;
  if (!hasAnyControl(setting)) return reported != null && reported.revision != null;
  return reported?.revision !== setting.revision;
};

/** "1.5× · −1.0 EV · box on (rev 3)": what a recording applied, and from which setting. */
export const describeReported = (reported: CameraControlsReported): string => {
  const zoom = reported.zoom_ratio != null ? formatZoom(reported.zoom_ratio) : 'phone zoom';
  const ev = reported.ev != null ? formatEv(reported.ev) : 'phone EV';
  const box = reported.metering_box ? 'box on' : 'box off';
  const source = reported.revision != null ? `rev ${reported.revision}` : "phone's own setup";
  return `${zoom} · ${ev} · ${box} (${source})`;
};
